import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "../../lib/api";
import { ArrowLeft, ArrowRight, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SurveyTake() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [survey, setSurvey] = useState(null);
    const [responseId, setResponseId] = useState(null);
    const [step, setStep] = useState(-1); // -1 = intro
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null); // {status, awarded_points, invalid_reason}
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get(`/surveys/${id}`)
            .then((r) => setSurvey(r.data))
            .catch((e) => setErr(errorMessage(e)));
    }, [id]);

    const start = async () => {
        try {
            const { data } = await api.post("/responses/start", { survey_id: id });
            setResponseId(data.id);
            // Pre-fill any saved answers
            const m = {};
            (data.answers || []).forEach((a) => { m[a.question_id] = a.value; });
            setAnswers(m);
            setStep(0);
        } catch (e) {
            setErr(errorMessage(e));
        }
    };

    const q = survey?.questions?.[step];
    const total = survey?.questions?.length || 0;
    const progress = Math.round(((step + 1) / Math.max(total, 1)) * 100);

    const setVal = (val) => setAnswers((a) => ({ ...a, [q.id]: val }));

    const saveAnswer = async () => {
        if (!responseId || !q) return;
        await api.post(`/responses/${responseId}/answer`, { question_id: q.id, value: answers[q.id] }).catch(() => {});
    };

    const next = async () => {
        if (q.required && (answers[q.id] === undefined || answers[q.id] === "" || (Array.isArray(answers[q.id]) && answers[q.id].length === 0))) {
            toast.error("This question is required");
            return;
        }
        await saveAnswer();
        // Branching: scan rules in order. `end` and `disqualify` short-circuit
        // immediately to /complete (server validates the same path). `skip_to`
        // can only jump forward; backwards/zero jumps fall through.
        let target = step + 1;
        let shortCircuit = null; // 'end' or 'disqualify'
        for (const r of q.branch_rules || []) {
            if (String(answers[q.id]) !== String(r.when_value)) continue;
            if (r.action === "disqualify") { shortCircuit = "disqualify"; break; }
            if (r.action === "end") { shortCircuit = "end"; break; }
            if (r.action === "skip_to" && r.target_question_id) {
                const idx = survey.questions.findIndex((x) => x.id === r.target_question_id);
                if (idx > step) { target = idx; break; }
            }
        }
        if (shortCircuit) return complete();
        if (target >= total) return complete();
        setStep(target);
    };

    const back = () => {
        if (step > 0) setStep(step - 1);
    };

    const complete = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post("/responses/complete", { response_id: responseId });
            setDone(data);
        } catch (e) {
            setErr(errorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    if (err) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-paper p-6">
                <div className="text-center">
                    <X size={32} className="mx-auto text-error mb-4" />
                    <p className="font-semibold mb-2">{err}</p>
                    <button className="btn btn-outline mt-4" onClick={() => navigate("/respondent/surveys")} data-testid="error-back-btn">
                        Back to surveys
                    </button>
                </div>
            </div>
        );
    }

    if (!survey) {
        return <div className="min-h-screen flex items-center justify-center bg-paper text-xs font-mono text-ink-600">LOADING…</div>;
    }

    if (done) {
        const ok = done.status === "completed";
        return (
            <div className="min-h-screen bg-paper flex flex-col items-center justify-center p-6 text-center">
                {ok ? <CheckCircle size={64} className="text-success mb-6" weight="fill" /> : <X size={64} className="text-error mb-6" />}
                <h1 className="h1 max-w-2xl mb-4">
                    {ok ? `+${done.awarded_points} pts earned` : done.status === "disqualified" ? "Thanks — you didn't qualify" : "Response invalid"}
                </h1>
                <p className="text-ink-600 max-w-lg mb-8">
                    {ok && "Your balance has been updated. Take another survey or cash out anytime above $10."}
                    {done.status === "disqualified" && "No points awarded for this study, but more surveys are waiting."}
                    {done.status === "invalid" && (
                        done.invalid_reason === "speed_check_failed"
                            ? "You moved through too quickly. Please take your time on future surveys."
                            : done.invalid_reason === "daily_cap_exceeded"
                                ? "You've already completed 5 surveys today — that's the daily limit. Come back tomorrow for more."
                                : "Some answers couldn't be validated."
                    )}
                </p>
                <div className="flex gap-3">
                    <button className="btn btn-respondent" onClick={() => navigate("/respondent/surveys")} data-testid="done-more-btn">Take another</button>
                    <button className="btn btn-outline" onClick={() => navigate("/respondent")} data-testid="done-dashboard-btn">Dashboard</button>
                </div>
            </div>
        );
    }

    if (step === -1) {
        return (
            <div className="min-h-screen bg-paper flex flex-col">
                <div className="max-w-3xl mx-auto px-6 py-16 flex-1 flex flex-col justify-center">
                    <button onClick={() => navigate(-1)} className="btn btn-ghost px-2 self-start mb-6" data-testid="intro-back-btn">
                        <ArrowLeft size={16} /> Back
                    </button>
                    <p className="eyebrow mb-3">{survey.category?.toUpperCase()} · {survey.advertiser_company || "STUDY"}</p>
                    <h1 className="h1 mb-6" data-testid="survey-intro-title">{survey.title}</h1>
                    <p className="text-ink-600 leading-relaxed mb-8">{survey.description}</p>
                    <div className="card-base divide-y divide-ink-200 mb-10">
                        <Row label="Reward" value={`+${survey.points_per_response} pts ($${(survey.points_per_response/100).toFixed(2)})`} />
                        <Row label="Estimated time" value={`~${survey.estimated_minutes} min`} />
                        <Row label="Questions" value={total} />
                        <Row label="Includes attention check" value={survey.questions.some(x => x.is_attention_check) ? "Yes" : "No"} />
                    </div>
                    <button onClick={start} className="btn btn-respondent w-full sm:w-auto" data-testid="start-survey-btn">
                        Start survey <ArrowRight size={18} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-paper flex flex-col">
            <div className="fixed top-0 left-0 right-0 h-1.5 bg-ink-200 z-50">
                <div className="h-full bg-respondent transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
            <div className="max-w-3xl mx-auto px-6 py-16 w-full flex-1 flex flex-col justify-center">
                <p className="font-mono text-[10px] text-ink-600 mb-6" data-testid="q-counter">QUESTION {step + 1} / {total}</p>
                <h2 className="h2 mb-10" data-testid="q-text">{q.text}{q.required ? <span className="text-respondent">*</span> : null}</h2>

                <QuestionInput q={q} value={answers[q.id]} onChange={setVal} />

                <div className="mt-12 flex justify-between items-center">
                    <button onClick={back} disabled={step === 0} className="btn btn-ghost" data-testid="q-back-btn">
                        <ArrowLeft size={16} /> Back
                    </button>
                    <button onClick={next} disabled={submitting} className="btn btn-ink" data-testid="q-next-btn">
                        {step + 1 === total ? (submitting ? "Submitting…" : "Submit") : "Next"} <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between items-center px-6 py-4">
            <span className="text-sm text-ink-600">{label}</span>
            <span className="font-mono">{value}</span>
        </div>
    );
}

function QuestionInput({ q, value, onChange }) {
    if (q.type === "single_choice" || q.type === "yes_no") {
        const opts = q.type === "yes_no" ? ["Yes", "No"] : (q.options || []);
        return (
            <div className="grid sm:grid-cols-2 gap-3">
                {opts.map((o) => (
                    <button key={o} type="button" onClick={() => onChange(o)} className={`option-card ${String(value) === String(o) ? "selected" : ""}`} data-testid={`opt-${o}`}>
                        {o}
                    </button>
                ))}
            </div>
        );
    }
    if (q.type === "multi_choice") {
        const arr = Array.isArray(value) ? value : [];
        const toggle = (o) => {
            const has = arr.includes(o);
            onChange(has ? arr.filter((x) => x !== o) : [...arr, o]);
        };
        return (
            <div className="grid sm:grid-cols-2 gap-3">
                {(q.options || []).map((o) => (
                    <button key={o} type="button" onClick={() => toggle(o)} className={`option-card ${arr.includes(o) ? "selected" : ""}`} data-testid={`opt-${o}`}>
                        {o}
                    </button>
                ))}
            </div>
        );
    }
    if (q.type === "rating") {
        const max = (q.options && q.options.length) || 5;
        const opts = Array.from({ length: Number(max) || 5 }, (_, i) => String(i + 1));
        return (
            <div className="flex gap-2 flex-wrap">
                {opts.map((o) => (
                    <button key={o} type="button" onClick={() => onChange(o)} className={`option-card !w-16 !text-center ${String(value) === o ? "selected" : ""}`} data-testid={`rating-${o}`}>
                        {o}
                    </button>
                ))}
            </div>
        );
    }
    if (q.type === "text") {
        return <input className="input-base text-lg" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="text-input" />;
    }
    if (q.type === "long_text") {
        return <textarea className="input-base text-lg min-h-[140px]" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="longtext-input" />;
    }
    if (q.type === "ranking") {
        // simplified: order options via reorder
        const list = Array.isArray(value) && value.length === (q.options || []).length ? value : (q.options || []);
        const move = (idx, dir) => {
            const next = [...list];
            const j = idx + dir;
            if (j < 0 || j >= next.length) return;
            [next[idx], next[j]] = [next[j], next[idx]];
            onChange(next);
        };
        return (
            <div className="space-y-2">
                {list.map((o, i) => (
                    <div key={o} className="flex items-center justify-between option-card">
                        <span><span className="font-mono mr-3 text-ink-600">#{i + 1}</span>{o}</span>
                        <span className="flex gap-2">
                            <button type="button" className="btn btn-ghost px-2 py-1" onClick={() => move(i, -1)} data-testid={`rank-up-${i}`}>↑</button>
                            <button type="button" className="btn btn-ghost px-2 py-1" onClick={() => move(i, 1)} data-testid={`rank-down-${i}`}>↓</button>
                        </span>
                    </div>
                ))}
            </div>
        );
    }
    if (q.type === "matrix") {
        const rows = q.matrix_rows || [];
        const cols = q.options || [];
        const obj = (typeof value === "object" && value && !Array.isArray(value)) ? value : {};
        const set = (r, c) => onChange({ ...obj, [r]: c });
        return (
            <div className="overflow-x-auto">
                <table className="w-full border border-ink-200 text-sm">
                    <thead>
                        <tr>
                            <th className="p-3 text-left bg-cream"></th>
                            {cols.map((c) => <th key={c} className="p-3 text-center bg-cream font-mono text-xs">{c}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r} className="border-t border-ink-200">
                                <td className="p-3">{r}</td>
                                {cols.map((c) => (
                                    <td key={c} className="p-3 text-center">
                                        <input type="radio" name={`m-${r}`} checked={obj[r] === c} onChange={() => set(r, c)} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }
    return null;
}
